import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Grid,
  Chip,
  Alert,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountBalance as BankIcon,
  Work as WorkIcon,
  Description as DocumentIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { contractorDetailsAPI } from "../services/api";
import {
  calculateProfileCompletion,
  getProfileCompletionMessage,
  getProfileCompletionColor,
} from "../utils/profileCompletion";
import ContractorProfileWizard from "../components/ContractorProfileWizard";

const Profile = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileProgress, setProfileProgress] = useState(0);
  const [showWizard, setShowWizard] = useState(false);

  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      // Try to get profile status first
      const statusResponse = await contractorDetailsAPI.getProfileStatus();
      if (statusResponse.data.success) {
        const statusData = statusResponse.data.data;
        setProfileData(statusData);
        const { completionPercentage } = calculateProfileCompletion(statusData);
        setProfileProgress(completionPercentage);
      } else {
        // Fallback to regular profile endpoint
        const response = await contractorDetailsAPI.getContractorProfile();
        if (response.data.success) {
          const data = response.data.data;
          setProfileData(data);
          const { completionPercentage } = calculateProfileCompletion(data);
          setProfileProgress(completionPercentage);
        } else {
          setProfileData({
            companyName: "",
            contractorId: "",
            registrationDetails: {},
            contactDetails: {},
            bankDetails: {},
            ownerDetails: {},
            licenseDetails: {},
          });
          setProfileProgress(0);
        }
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      enqueueSnackbar("Failed to load profile data", { variant: "error" });
      setProfileData({
        companyName: "",
        contractorId: "",
        registrationDetails: {},
        contactDetails: {},
        bankDetails: {},
        ownerDetails: {},
        licenseDetails: {},
      });
      setProfileProgress(0);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const handleWizardComplete = () => {
    loadProfileData();
    setShowWizard(false);
  };

  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading profile...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Contractor Profile
        </Typography>
        <Button
          variant="contained"
          startIcon={<DocumentIcon />}
          onClick={() => setShowWizard(true)}
        >
          Complete Profile
        </Button>
      </Box>

      {/* Profile Progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Profile Completion Progress
          </Typography>
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={profileProgress}
              color={getProfileCompletionColor(profileProgress)}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {profileProgress}% Complete
              </Typography>
              <Chip
                label={profileProgress >= 100 ? "Complete" : "Incomplete"}
                color={profileProgress >= 100 ? "success" : "warning"}
                size="small"
              />
            </Box>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {getProfileCompletionMessage(profileProgress)}
          </Alert>
        </CardContent>
      </Card>

      {/* Profile Details Display */}
      {profileData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Company Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <BusinessIcon />
                  <Typography variant="h6">Company Details</Typography>
                  {profileData.companyName && profileData.contractorId ? (
                    <CheckIcon color="success" />
                  ) : (
                    <WarningIcon color="warning" />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Company Name
                    </Typography>
                    <Typography variant="body2">
                      {profileData.companyName || "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Contractor ID
                    </Typography>
                    <Typography variant="body2">
                      {profileData.contractorId || "Not provided"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <PersonIcon />
                  <Typography variant="h6">Contact Details</Typography>
                  {profileData.contactDetails?.primaryContact?.name &&
                  profileData.contactDetails?.primaryContact?.email ? (
                    <CheckIcon color="success" />
                  ) : (
                    <WarningIcon color="warning" />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Contact Name
                    </Typography>
                    <Typography variant="body2">
                      {profileData.contactDetails?.primaryContact?.name ||
                        "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2">
                      {profileData.contactDetails?.primaryContact?.email ||
                        "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body2">
                      {profileData.contactDetails?.primaryContact?.phone ||
                        "Not provided"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Registration Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <DocumentIcon />
                  <Typography variant="h6">Registration Details</Typography>
                  {profileData.registrationDetails?.panNumber &&
                  profileData.registrationDetails?.gstNumber ? (
                    <CheckIcon color="success" />
                  ) : (
                    <WarningIcon color="warning" />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      PAN Number
                    </Typography>
                    <Typography variant="body2">
                      {profileData.registrationDetails?.panNumber ||
                        "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      GST Number
                    </Typography>
                    <Typography variant="body2">
                      {profileData.registrationDetails?.gstNumber ||
                        "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Business Type
                    </Typography>
                    <Typography variant="body2">
                      {profileData.registrationDetails?.businessType ||
                        "Not provided"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Bank Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <BankIcon />
                  <Typography variant="h6">Bank Details</Typography>
                  {profileData.bankDetails?.accountName &&
                  profileData.bankDetails?.accountNumber ? (
                    <CheckIcon color="success" />
                  ) : (
                    <WarningIcon color="warning" />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Name
                    </Typography>
                    <Typography variant="body2">
                      {profileData.bankDetails?.accountName || "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Number
                    </Typography>
                    <Typography variant="body2">
                      {profileData.bankDetails?.accountNumber || "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Bank Name
                    </Typography>
                    <Typography variant="body2">
                      {profileData.bankDetails?.bankName || "Not provided"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Owner Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <PersonIcon />
                  <Typography variant="h6">Owner Details</Typography>
                  {profileData.ownerDetails?.ownerName &&
                  profileData.ownerDetails?.aadhaarNumber ? (
                    <CheckIcon color="success" />
                  ) : (
                    <WarningIcon color="warning" />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Owner Name
                    </Typography>
                    <Typography variant="body2">
                      {profileData.ownerDetails?.ownerName || "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Aadhaar Number
                    </Typography>
                    <Typography variant="body2">
                      {profileData.ownerDetails?.aadhaarNumber ||
                        "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body2">
                      {profileData.ownerDetails?.phoneNumber || "Not provided"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* License Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <WorkIcon />
                  <Typography variant="h6">License Details</Typography>
                  {profileData.licenseDetails?.laborLicenseNumber &&
                  profileData.licenseDetails?.licenseType ? (
                    <CheckIcon color="success" />
                  ) : (
                    <WarningIcon color="warning" />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      License Number
                    </Typography>
                    <Typography variant="body2">
                      {profileData.licenseDetails?.laborLicenseNumber ||
                        "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      License Type
                    </Typography>
                    <Typography variant="body2">
                      {profileData.licenseDetails?.licenseType ||
                        "Not provided"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Max Workers
                    </Typography>
                    <Typography variant="body2">
                      {profileData.licenseDetails?.maxWorkersAllowed ||
                        "Not provided"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Documents Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <DocumentIcon />
                  <Typography variant="h6">Documents</Typography>
                  {profileData.documents &&
                  Object.keys(profileData.documents).length > 0 ? (
                    <CheckIcon color="success" />
                  ) : (
                    <WarningIcon color="warning" />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {profileData.documents &&
                  Object.keys(profileData.documents).length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {Object.entries(profileData.documents).map(
                        ([docType, docInfo]) => (
                          <Box
                            key={docType}
                            sx={{
                              p: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              minWidth: 200,
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {docType
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {docInfo.filename || "Document uploaded"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {docInfo.uploadedAt
                                ? new Date(
                                    docInfo.uploadedAt
                                  ).toLocaleDateString()
                                : ""}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <DocumentIcon
                        sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No documents uploaded yet
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Upload your business documents to complete your profile
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Profile Wizard */}
      <ContractorProfileWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onComplete={handleWizardComplete}
      />
    </Box>
  );
};

export default Profile;
